import type { AgentSurfaceT, AgentT } from "@malfak/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import { Bot, BotMessageSquare, BotOff, ChevronDown, Settings2, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Segmented } from "@/components/ui/controls";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuSeparator, MenuTrigger, Tooltip } from "@/components/ui/overlays";
import { PanelSection } from "@/components/ui/quick-view";
import { patchItems } from "@/features/inbox/model";
import { useApiMutation, useApiQuery } from "@/lib/api/hooks";
import { useT } from "@/lib/i18n";
import type { Comment, Conversation } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useAgentRole, useAgents, useRefreshAgents } from "./hooks";
import { agentLabel, handledByText, pickerBody, pickerValue, sourceText, type PickerValue } from "./model";

const active = (list: AgentT[] | undefined) => (list ?? []).filter((a) => a.status === "active");

/**
 * DM header: "Assigned agent" picker (replaces the plain AI on/off switch, same meaning: Off = no AI replies here;
 * the default / a specific agent = AI answers) + "Draft with agent".
 */
export function ConversationAgentPicker({ conversation }: { conversation: Conversation }) {
  const { t } = useT();
  const navigate = useNavigate();
  const agents = useAgents();
  const { write } = useAgentRole();
  const refresh = useRefreshAgents();
  const value = pickerValue(conversation.agent, conversation.aiAgent);
  const resolved = conversation.agent?.resolved ?? null;
  const off = value === "off";
  const m = useApiMutation("updateConversationAgent", {
    onSuccess: ({ agent }) => {
      patchItems([conversation.id], { conversation: { agent, aiAgent: agent.resolved !== null } });
      toast.success(agent.mode === "off" ? t("agents.picker.turnedOff") : t("agents.picker.updated"), { duration: 1800 });
      void refresh();
    },
    onError: (e) => toast.error(e.problem?.title ?? t("agents.picker.failed")),
  });
  const label = off ? t("agents.picker.off") : resolved ? agentLabel(resolved) : t("agents.picker.inherit");
  return (
    <span className="inline-flex shrink-0 items-center gap-1">
      <Menu>
        <Tooltip content={handledByText(conversation.agent, t)}>
          <MenuTrigger asChild>
            <button
              type="button"
              disabled={!write}
              data-testid="agent-picker"
              data-value={value}
              aria-label={`${t("agents.picker.label")}: ${label}`}
              className={cn(
                "inline-flex h-8 max-w-[220px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-[12px] font-medium transition disabled:opacity-60",
                off || !resolved ? "border-line text-ink-2 hover:border-line-2" : "border-violet/40 bg-violet-soft text-violet",
              )}
            >
              {off ? <BotOff className="size-4" /> : <Bot className="size-4" />}
              <span className="hidden truncate 2xl:inline">{label}</span>
              <ChevronDown className="size-3.5 opacity-70" />
            </button>
          </MenuTrigger>
        </Tooltip>
        <MenuContent className="w-72">
          <p className="px-2.5 pt-1.5 pb-2 text-[12px] leading-snug text-ink-2" data-testid="agent-handled-by">
            {handledByText(conversation.agent, t)}
          </p>
          <MenuSeparator />
          <MenuLabel>{t("agents.picker.label")}</MenuLabel>
          <MenuRadioGroup value={value} onValueChange={(v) => m.mutate({ params: { conversationId: conversation.id }, body: pickerBody(v as PickerValue) })}>
            <MenuRadioItem value="off" icon={<BotOff />}>
              {t("agents.picker.off")}
            </MenuRadioItem>
            <MenuRadioItem value="inherit" icon={<Bot />}>
              {t("agents.picker.inherit")}
            </MenuRadioItem>
            {active(agents.data).map((a) => (
              <MenuRadioItem key={a.id} value={a.id} icon={<span aria-hidden>{a.emoji ?? "🤖"}</span>}>
                {a.name}
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
          <MenuSeparator />
          <MenuItem icon={<Settings2 />} onSelect={() => void navigate({ to: "/automation/agents" })}>
            {t("agents.picker.manage")}
          </MenuItem>
        </MenuContent>
      </Menu>
      <DraftWithAgent target={{ type: "conversation", id: conversation.id }} resolvedId={off ? null : (resolved?.agentId ?? null)} compact />
    </span>
  );
}

/**
 * "Draft with agent": the resolved agent first, then every other active agent. Creates an approval (never sends) and
 * offers to open it.
 */
export function DraftWithAgent({ target, resolvedId, surface, compact }: { target: { type: "comment" | "conversation"; id: string }; resolvedId: string | null; surface?: AgentSurfaceT; compact?: boolean }) {
  const { t } = useT();
  const navigate = useNavigate();
  const agents = useAgents();
  const { write } = useAgentRole();
  const refresh = useRefreshAgents();
  const list = active(agents.data).sort((a, b) => (a.id === resolvedId ? -1 : b.id === resolvedId ? 1 : 0));
  const draft = useApiMutation("agentDraft", {
    onSuccess: (r) => {
      const name = r.agent ? agentLabel(r.agent) : "";
      if (r.approvalId) toast.success(t("agents.draft.done", { agent: name }), { action: { label: t("agents.draft.open"), onClick: () => void navigate({ to: "/approvals" }) } });
      else toast.info(t("agents.draft.skipped", { agent: name, reason: r.run.reason ? t(`agents.reasons.${r.run.reason}`) : "" }));
      void refresh();
    },
    onError: (e) => toast.error(e.problem?.detail ?? e.problem?.title ?? t("agents.draft.failed")),
  });
  const run = (agentId: string) => draft.mutate({ body: { target, agentId, ...(surface ? { surface } : {}) } });
  if (!write) return null;
  return (
    <Menu>
      <MenuTrigger asChild>
        {compact ? (
          <Button size="icon-sm" variant="ghost" aria-label={t("agents.draft.button")} loading={draft.isPending} disabled={!list.length} data-testid="draft-with-agent">
            <Sparkles />
          </Button>
        ) : (
          <Button size="sm" variant="outline" icon={<Sparkles />} iconEnd={<ChevronDown />} loading={draft.isPending} disabled={!list.length} data-testid="draft-with-agent">
            {draft.isPending ? t("agents.draft.drafting") : t("agents.draft.button")}
          </Button>
        )}
      </MenuTrigger>
      <MenuContent className="w-64">
        <MenuLabel>{t("agents.draft.pick")}</MenuLabel>
        {list.map((a) => (
          <MenuItem key={a.id} icon={<span aria-hidden>{a.emoji ?? "🤖"}</span>} onSelect={() => run(a.id)} data-testid="draft-agent-option">
            {a.name}
            {a.id === resolvedId && (
              <Badge size="xs" tone="violet" className="ms-1.5">
                {t("agents.sources.any")}
              </Badge>
            )}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}

/** Comment quick panel / inbox: who answers this comment (resolved, read-only with its source) + "Draft with agent". */
export function CommentAgentCard({ comment }: { comment: Pick<Comment, "id" | "byAccount" | "status"> }) {
  const { t } = useT();
  const [surface, setSurface] = useState<"comment_public" | "comment_private">("comment_public");
  const q = useApiQuery("resolveAgent", { query: { type: "comment", id: comment.id, surface } }, { staleTime: 15_000 });
  if (comment.byAccount || comment.status === "deleted") return null;
  const r = q.data?.agent ?? null;
  return (
    <PanelSection title={t("agents.comment.title")}>
      <div className="space-y-2.5 rounded-2xl border border-line p-3" data-testid="comment-agent">
        <p className="flex items-start gap-2 text-[12.5px] text-ink-2" data-testid="comment-agent-resolved">
          <BotMessageSquare className="mt-0.5 size-4 shrink-0 text-violet" aria-hidden />
          {q.isPending ? "…" : r ? (
            <span>
              {t("agents.comment.handledBy", { agent: agentLabel(r), source: sourceText(r, t) })}{" "}
              <Link to="/automation/agents/$agentId" params={{ agentId: r.agentId }} className="font-medium text-accent hover:underline">
                {t("agents.list.edit")}
              </Link>
            </span>
          ) : (
            <span>{t("agents.comment.none")}</span>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented size="sm" value={surface} onChange={setSurface} label={t("agents.playground.surface")} options={(["comment_public", "comment_private"] as const).map((s) => ({ value: s, label: t(`agents.surfaces.${s}`) }))} />
          <DraftWithAgent target={{ type: "comment", id: comment.id }} resolvedId={r?.agentId ?? null} surface={surface} />
        </div>
      </div>
    </PanelSection>
  );
}

/** Approvals: "Drafted by 🛍️ Sales agent v3". */
export function ApprovalAgentBadge({ agent }: { agent: { id: string; name: string; emoji: string | null; version: number } | null | undefined }) {
  const { t } = useT();
  if (!agent) return null;
  return (
    <Link
      to="/automation/agents/$agentId"
      params={{ agentId: agent.id }}
      className="inline-flex max-w-[240px] items-center gap-1 rounded-lg bg-violet-soft px-2 py-0.5 text-[12px] font-medium text-violet hover:brightness-95"
      data-testid="approval-agent"
    >
      <span className="truncate">{t("agents.approval.draftedBy", { agent: agentLabel(agent), version: agent.version })}</span>
    </Link>
  );
}
